'use client'

import { useTranslations, useLocale } from 'next-intl'
import Link from 'next/link'
import { motion } from 'framer-motion'

export default function ProductsSection() {
  const t = useTranslations('products')
  const locale = useLocale()

  const products = [
    {
      key: 'aipuna',
      href: `/${locale}/products/aipuna`,
      gradient: 'from-primary-500 to-primary-700',
      statusColor: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    },
    {
      key: 'janus',
      href: `/${locale}/products/janus`,
      gradient: 'from-secondary-600 to-secondary-800',
      statusColor: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    },
    {
      key: 'octochat',
      href: `/${locale}/products/octochat`,
      gradient: 'from-primary-600 to-secondary-700',
      statusColor: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    },
    {
      key: 'learncoach',
      href: `/${locale}/products/learncoach`,
      gradient: 'from-teal-500 to-primary-700',
      statusColor: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    },
    {
      key: 'agodoc',
      href: `/${locale}/products/agodoc`,
      gradient: 'from-secondary-600 to-primary-600',
      statusColor: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    },
  ]

  return (
    <section id="products" className="section-padding bg-white dark:bg-secondary-900">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-secondary-900 dark:text-white">
            {t('sectionTitle')}
          </h2>
          <p className="text-base sm:text-lg text-secondary-600 dark:text-secondary-300 max-w-3xl mx-auto">
            {t('sectionSubtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {products.map((product, index) => (
            <motion.div
              key={product.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              viewport={{ once: true }}
              whileHover={{ y: -8 }}
              className="bg-white dark:bg-secondary-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
            >
              <div className={`h-2 bg-gradient-to-r ${product.gradient}`} />
              <div className="p-6 sm:p-8">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-bold text-secondary-900 dark:text-white">
                    {t(`${product.key}.name`)}
                  </h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${product.statusColor}`}>
                    {t(`${product.key}.status`)}
                  </span>
                </div>
                <p className="text-secondary-600 dark:text-secondary-400 mb-6 leading-relaxed">
                  {t(`${product.key}.description`)}
                </p>
                <Link
                  href={product.href}
                  className="inline-flex items-center text-primary-600 dark:text-primary-400 font-semibold hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                >
                  {t(`${product.key}.cta`)}
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
